import React from 'react';
import { FONT } from '../constants';
import { C } from '../styles';

interface SectionHeadProps { title: string; subtitle: string; }
export function SectionHead({ title, subtitle }: SectionHeadProps) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h2 style={{ fontSize: 26, fontWeight: 700, color: C.ink, letterSpacing: '-0.015em', marginBottom: 6 }}>{title}</h2>
      <p style={{ fontSize: 14, color: C.ink, lineHeight: 1.55, maxWidth: 560, fontWeight: 500, margin: 0 }}>{subtitle}</p>
    </div>
  );
}

interface FieldProps {
  label: string;
  help?: string;
  children: React.ReactNode;
  error?: string | null;
}
export function Field({ label, help, children, error }: FieldProps) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: C.ink, display: 'block', marginBottom: 5 }}>{label}</label>
      {children}
      {error && <div style={{ fontSize: 11.5, color: '#C41E1E', marginTop: 5, lineHeight: 1.5, fontWeight: 500 }}>{error}</div>}
      {!error && help && <div style={{ fontSize: 11.5, color: C.muted, marginTop: 5, lineHeight: 1.5 }}>{help}</div>}
    </div>
  );
}

interface TextInputProps {
  value: string | number | null | undefined;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  maxLength?: number;
  error?: string | null;
}
export function TextInput({ value, onChange, placeholder, type = 'text', maxLength, error }: TextInputProps) {
  const borderColor = error ? '#C41E1E' : C.line;
  return (
    <input
      type={type}
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      style={{
        width: '100%', fontFamily: FONT, fontSize: 13.5, color: C.ink,
        padding: '9px 12px', border: '1px solid ' + borderColor,
        borderRadius: 6, background: '#fff', outline: 'none',
      }}
      onFocus={(e) => {
        (e.target as HTMLInputElement).style.borderColor = error ? '#C41E1E' : C.teal;
        (e.target as HTMLInputElement).style.boxShadow = error ? '0 0 0 3px rgba(196,30,30,0.15)' : '0 0 0 3px ' + C.tealSoft;
      }}
      onBlur={(e) => {
        (e.target as HTMLInputElement).style.borderColor = borderColor;
        (e.target as HTMLInputElement).style.boxShadow = 'none';
      }}
    />
  );
}

interface SelectInputProps {
  value: string | number | null | undefined;
  onChange: (v: string) => void;
  children: React.ReactNode;
}
export function SelectInput({ value, onChange, children }: SelectInputProps) {
  return (
    <select
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: '100%', fontFamily: FONT, fontSize: 13.5, color: C.ink,
        padding: '9px 12px', border: '1px solid ' + C.line,
        borderRadius: 6, background: '#fff', outline: 'none', cursor: 'pointer',
      }}
    >
      {children}
    </select>
  );
}

interface CardSectionProps {
  label: string;
  children: React.ReactNode;
}
export function CardSection({ label, children }: CardSectionProps) {
  return (
    <div style={{
      background: '#fff', border: '1px solid ' + C.line, borderRadius: 12,
      padding: '24px 28px', marginBottom: 16,
    }}>
      <div style={{
        fontSize: 12, fontWeight: 700, color: C.ink,
        letterSpacing: '0.08em', textTransform: 'uppercase',
        marginBottom: 16, paddingBottom: 10,
        borderBottom: '1.5px solid ' + C.ink,
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

interface SaveBarProps {
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
  label?: string;
}
export function SaveBar({ dirty, saving, onSave, onDiscard, label = 'Save' }: SaveBarProps) {
  return (
    <div style={{
      position: 'sticky', bottom: 0, background: '#fff',
      borderTop: '1px solid ' + C.line,
      padding: '14px 26px', margin: '24px -26px -24px',
      borderRadius: '0 0 10px 10px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14,
    }}>
      <span style={{ fontSize: 12.5, color: dirty ? C.amber : C.muted }}>
        {saving ? 'Saving...' : dirty ? 'You have unsaved changes' : 'All changes saved'}
      </span>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={onDiscard}
          disabled={!dirty || saving}
          style={{
            background: '#fff', color: dirty ? C.text : C.faint,
            border: '1px solid ' + C.line, borderRadius: 6,
            padding: '8px 16px', fontWeight: 500, fontSize: 13,
            cursor: dirty ? 'pointer' : 'not-allowed', fontFamily: FONT,
          }}
        >
          Discard
        </button>
        <button
          onClick={onSave}
          disabled={!dirty || saving}
          style={{
            background: dirty ? C.teal : C.line,
            color: dirty ? '#fff' : C.faint,
            border: 'none', borderRadius: 6,
            padding: '8px 16px', fontWeight: 500, fontSize: 13,
            cursor: dirty && !saving ? 'pointer' : 'not-allowed', fontFamily: FONT,
          }}
        >
          {saving ? 'Saving...' : label}
        </button>
      </div>
    </div>
  );
}